import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorText } from "@/lib/api";
import { FORM_SERVICES } from "@/lib/content";

const EMPTY = { name: "", email: "", phone: "", service: "", event_date: "", guests: "", message: "", consent: false };
const inputCls = "w-full rounded-xl border border-ink/15 bg-white/80 px-4 py-3 text-sm outline-none focus:border-granate focus:ring-2 focus:ring-granate/15 transition-colors";
const Label = ({ children }) => <span className="block mb-2 font-brand text-[10px] font-semibold tracking-[0.18em] uppercase text-ink/70">{children}</span>;

export const BookingForm = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.service) return setError("Elige un servicio.");
    if (!f.consent) return setError("Debes aceptar la política de privacidad.");
    setState("sending"); setError("");
    try {
      await api.post("/appointments", { ...f, guests: f.guests ? Number(f.guests) : null, event_date: f.event_date || null });
      setState("ok"); setF(EMPTY);
    } catch (err) {
      setState("idle");
      setError(err?.response ? errorText(err) : "No hemos podido enviar la solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.");
    }
  };

  if (state === "ok")
    return (
      <div className="rounded-[28px] bg-white p-10 text-center shadow-xl" data-testid="booking-success">
        <CheckCircle2 className="w-12 h-12 text-granate mx-auto" />
        <h3 className="mt-5 font-display text-4xl">¡Solicitud recibida!</h3>
        <p className="mt-3 text-ink/70">Gracias. Te hemos enviado un email de confirmación y te responderemos en un día laborable.</p>
        <button onClick={() => setState("idle")} className="mt-8 rounded-full border border-ink/20 px-6 py-3 font-brand text-[11px] font-semibold tracking-[0.18em] uppercase hover:bg-ink hover:text-white transition-colors" data-testid="booking-again-btn">Enviar otra solicitud</button>
      </div>
    );

  return (
    <form onSubmit={submit} className="rounded-[28px] bg-white/90 backdrop-blur p-6 sm:p-10 shadow-xl grid sm:grid-cols-2 gap-5" data-testid="booking-form">
      <label className="sm:col-span-2"><Label>Nombre completo *</Label><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" /></label>
      <label><Label>Email *</Label><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" /></label>
      <label><Label>Teléfono *</Label><input required type="tel" minLength={6} value={f.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" /></label>
      <div className="sm:col-span-2">
        <Label>Servicio de interés *</Label>
        <Select value={f.service} onValueChange={set("service")}>
          <SelectTrigger className="h-[46px] rounded-xl border-ink/15 bg-white/80" data-testid="booking-service-select"><SelectValue placeholder="Elige un servicio" /></SelectTrigger>
          <SelectContent className="bg-white">
            {FORM_SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`booking-service-option-${s}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <label><Label>Fecha del evento</Label><input type="date" value={f.event_date} onChange={set("event_date")} className={inputCls} data-testid="booking-date-input" /></label>
      <label><Label>Nº de invitados</Label><input type="number" min={1} value={f.guests} onChange={set("guests")} className={inputCls} data-testid="booking-guests-input" /></label>
      <label className="sm:col-span-2"><Label>Cuéntanos tu idea</Label><textarea rows={4} value={f.message} onChange={set("message")} placeholder="Tipo de evento, lugar, estilo que te imaginas..." className={inputCls} data-testid="booking-message-input" /></label>
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-ink/70 cursor-pointer">
        <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5" data-testid="booking-consent-checkbox" />
        Acepto la política de privacidad y el tratamiento de mis datos para gestionar la cita.
      </label>
      {error && <p className="sm:col-span-2 text-sm text-destructive" data-testid="booking-error">{error}</p>}
      <button type="submit" disabled={state === "sending"} className="btn-shine sm:col-span-2 inline-flex justify-center items-center gap-2 rounded-full bg-granate text-white py-4 font-brand text-xs font-semibold tracking-[0.18em] uppercase shadow-lg shadow-granate/30 hover:bg-[#7f1625] disabled:opacity-60 transition-colors" data-testid="booking-submit-btn">
        {state === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : "Agenda una cita"}
      </button>
    </form>
  );
};
